import React, { useState, useEffect, useCallback } from "react";
import { useRouteMatch } from "react-router-dom";
import cx from "classnames";

import ButtonCard from "wonderbly-components/lib/ButtonCard";
import Button from "wonderbly-components/lib/Button";
import CarouselButtonCard from "wonderbly-components/lib/CarouselButtonCard";
import Loading from "wonderbly-components/lib/Loading";

import PriceBadge from "./PriceBadge";
import { filterFormatsForLocale, getCarouselSlides } from "./helpers";
import {
  useModel,
  useMediaQueries,
  useQueryParams,
  useCart,
} from "../../hooks";
import buildProductUrl from "../../utils/buildProductUrl";
import { shouldRedirectToOptions } from "../CanvasDrawers/helpers";
import { replaceCanvasText } from "src/client/js/view/components/Canvas/helpers";

import "./GenericFormatSelector.scss";

const GenericFormatSelector = () => {
  const {
    productFormats,
    prismicProduct,
    countryModel,
    eagleProduct,
    eagleModel,
    prismicGlobalContentModel,
  } = useModel();
  const { params, setParamsReplaceHistory } = useQueryParams();
  const mobile = useMediaQueries("mobile");
  const [selectedFormat, setSelectedFormat] = useState(
    params.format_identifier
  );
  const [, setSelectedCover] = useState(params.cover);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { addItemToCart, editItemInCart, cartStatus, setFlashMessage } =
    useCart();
  const cartQueueHasItems = cartStatus.request.queue.length > 0;
  const routerParams = useRouteMatch().params;

  useEffect(() => {
    setIsSubmitting(cartQueueHasItems);
  }, [cartQueueHasItems]);

  const onHandleClick = useCallback(async (formatId) => {
    setSelectedFormat(formatId);
    // We are using the Eagle variant.formatIdentifier (e.g. a4-landscape:softback)
    const [size, cover] = formatId.split(":");
    setSelectedCover(cover);
    setParamsReplaceHistory({ format_identifier: formatId, cover, size });
  });

  const handleSubmit = async () => {
    setIsSubmitting(true);
    const customisations = params;
    const productId = prismicProduct.main.productId;

    const productData = {
      productId,
      country: { ...countryModel },
      productCustomisation: customisations,
    };

    const productUrlData = {
      model: { eagleProduct, eagleModel, countryModel },
      queryParams: customisations,
    };

    const shouldRedirect = await shouldRedirectToOptions(productData);
    if (routerParams.itemId) {
      shouldRedirect
        ? (window.location = buildProductUrl(productUrlData, {
            type: "options",
            query: { addonPage: 1 },
            itemId: routerParams.itemId,
          }))
        : editItemInCart(
            {
              id: routerParams.itemId,
              customisations,
            },
            true
          );
    } else {
      shouldRedirect
        ? (window.location = buildProductUrl(productUrlData, {
            type: "options",
            query: { addonPage: 1 },
          }))
        : addItemToCart({
            productId,
            customisations,
          });
    }
  };

  const replacementTextData = {
    queryParams: params,
    productCustomisation: params,
    prismicProduct,
    productId: prismicProduct.main.productId,
  };

  if (routerParams.itemId) {
    setFlashMessage(
      replaceCanvasText(
        prismicProduct.canvas.editingBanner,
        replacementTextData
      )
    );
  }

  return (
    <section className="GenericFormatSelector">
      <div className="GenericFormatSelector__wrapper container-guttered">
        <div className="GenericFormatSelector__content">
          {prismicGlobalContentModel.aovPagesTitles.formatTitle && (
            <h1 className="text-tera">
              {prismicGlobalContentModel.aovPagesTitles.formatTitle}
            </h1>
          )}
          {prismicGlobalContentModel.aovPagesTitles.formatSubtitle && (
            <p>{prismicGlobalContentModel.aovPagesTitles.formatSubtitle}</p>
          )}
        </div>
        <div className="GenericFormatSelector__format-items">
          {filterFormatsForLocale(
            productFormats,
            prismicGlobalContentModel,
            countryModel
          ).map((item, index) => {
            return (
              <div
                data-testid={item.title.toLowerCase().split(" ").join("-")}
                key={index}
                className={cx("GenericFormatSelector__format-items--item", {
                  "has-margin-top": !mobile,
                  "has-no-badge": !item.badgeLabelOverride && !item.badge,
                })}
              >
                {(!item.formatIsCarousel && mobile) ||
                  (item.savings && (
                    <div className="GenericFormatSelector__format-savings">
                      <PriceBadge />
                      <span className="GenericFormatSelector__format-savings-price--text">
                        <span className="GenericFormatSelector__format-savings-was-price--text">
                          {item.savings.was_price.formatted}
                        </span>
                        {item.price.formatted}
                      </span>
                    </div>
                  ))}
                {item.formatIsCarousel ? (
                  <CarouselButtonCard
                    slides={getCarouselSlides(item, true)}
                    badge={
                      item.badgeLabelOverride
                        ? item.badgeLabelOverride
                        : item.badge
                    }
                    title={item.title}
                    body={item.description}
                    price={item.price.formatted}
                    cta={item.cta}
                    ctaSelected={item.ctaSelected}
                    onHandleClick={() => onHandleClick(item.formatId)}
                    isSelected={item.formatId === selectedFormat}
                  />
                ) : (
                  <ButtonCard
                    image={getCarouselSlides(item)}
                    badge={
                      item.badgeLabelOverride
                        ? item.badgeLabelOverride
                        : item.badge
                    }
                    title={item.title}
                    body={item.description}
                    price={item.price.formatted}
                    cta={item.cta}
                    ctaSelected={item.ctaSelected}
                    onHandleClick={() => onHandleClick(item.formatId)}
                    isSelected={item.formatId === selectedFormat}
                  />
                )}
              </div>
            );
          })}
        </div>
      </div>
      <div className="GenericFormatSelector__actions">
        <div className="container-guttered">
          <Button onClick={handleSubmit} disabled={isSubmitting} type="button">
            {isSubmitting && <Loading xsmall relative />}
            {!isSubmitting &&
              prismicProduct.canvas.cta &&
              prismicProduct.canvas.cta}
            {!isSubmitting && !prismicProduct.canvas.cta && "Continue"}
          </Button>
        </div>
      </div>
    </section>
  );
};

export default GenericFormatSelector;
